import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { MONTHS, WEEKDAYS } from "@/lib/constants";
import { fileUrl } from "@/lib/api";
import { StatusDot } from "@/components/StatusDot";
import { MediaThumb } from "@/components/MediaThumb";
import { todayMonth, todayStr } from "@/lib/format";
import { ChevronLeft, ChevronRight } from "lucide-react";

// Reusable client month view (contact sheet). Fills its container width.
export function ClientCalendar({ posts, activeId, onSelect, initialMonth }) {
  const minMonth = todayMonth();
  const today = todayStr();
  const [month, setMonth] = useState(initialMonth && initialMonth >= minMonth ? initialMonth : minMonth);
  const shiftMonth = (dir) => {
    const [yy, mm] = month.split("-").map(Number);
    const d = new Date(yy, mm - 1 + dir, 1);
    const nm = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    if (nm < minMonth) return;
    setMonth(nm);
  };
  const atMin = month <= minMonth;
  useEffect(() => {
    if (initialMonth && initialMonth >= minMonth) setMonth(initialMonth);
    // eslint-disable-next-line
  }, [initialMonth]);
  const [y, m] = month.split("-").map(Number);
  const daysInMonth = new Date(y, m, 0).getDate();
  const startWeekday = (new Date(y, m - 1, 1).getDay() + 6) % 7;
  const byDay = {};
  posts.forEach((p) => { if (p.date.startsWith(month)) { const d = Number(p.date.split("-")[2]); (byDay[d] = byDay[d] || []).push(p); } });
  const cells = [];
  for (let i = 0; i < startWeekday; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  return (
    <div data-testid="client-month">
      <div className="flex items-center gap-3 mb-3">
        <button onClick={() => shiftMonth(-1)} disabled={atMin} data-testid="client-prev-month" className="w-8 h-8 border border-line rounded-full flex items-center justify-center hover:bg-app disabled:opacity-30"><ChevronLeft size={16} /></button>
        <div className="font-display text-2xl min-w-[160px]">{MONTHS[m - 1]} {y}</div>
        <button onClick={() => shiftMonth(1)} data-testid="client-next-month" className="w-8 h-8 border border-line rounded-full flex items-center justify-center hover:bg-app"><ChevronRight size={16} /></button>
      </div>
      <div className="grid grid-cols-7 gap-1.5 sm:gap-2 mb-2">{WEEKDAYS.map((w) => <div key={w} className="text-[10px] text-subtle font-data text-center">{w}</div>)}</div>
      <div className="grid grid-cols-7 gap-1.5 sm:gap-2">
        {cells.map((day, i) => (
          <motion.div key={i} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: Math.min(i * 0.008, 0.3) }}
            className={`aspect-square relative rounded-xl sm:rounded-2xl overflow-hidden ${day ? "bg-app border border-line" : ""} ${day && `${month}-${String(day).padStart(2, "0")}` < today ? "opacity-40" : ""}`}>
            {day && <>
              <span className="absolute top-1 left-1.5 font-data text-[9px] sm:text-[11px] text-white mix-blend-difference z-10">{String(day).padStart(2, "0")}</span>
              {(byDay[day] || []).map((p, idx) => {
                const active = p.id === activeId;
                const ring = (p.status === "aprobado" || p.status === "publicado") ? "#3F6B4F" : p.status === "cambios_solicitados" ? "#C2410C" : null;
                return (
                  <button key={p.id} onClick={() => onSelect(p.id)} data-testid={`cal-post-${p.id}`} className="absolute inset-0" style={{ zIndex: idx + 1 }}>
                    <MediaThumb post={p} emptyLabel="" />
                    {ring && !active && <span className="absolute inset-0 rounded-xl sm:rounded-2xl pointer-events-none" style={{ boxShadow: `inset 0 0 0 2px ${ring}` }} data-testid={`cal-ring-${p.status}`} />}
                    {active && <span className="absolute inset-0 rounded-xl sm:rounded-2xl pointer-events-none" style={{ boxShadow: "inset 0 0 0 3px var(--brand-color)" }} />}
                    {p.status === "enviado_cliente" && <span className="absolute bottom-1 right-1"><StatusDot color="#16161A" size={7} /></span>}
                  </button>
                );
              })}
            </>}
          </motion.div>
        ))}
      </div>
    </div>
  );
}
